import React, { useEffect, useState } from 'react';
import FERPA from '../../../../../components/FERPA/FerpaComponent';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMessagesQuestion } from '@fortawesome/pro-solid-svg-icons';
import TableSkeleton from '../../../../../components/SkeletonLoader/TableSkeleton';
import Skeleton from 'react-loading-skeleton';
import { faCheck, faClose, faChartArea } from '@fortawesome/free-solid-svg-icons';
import { DateFormatter } from '../../../../../utils/DateFormatter';
import { NavLink } from 'react-router-dom';
import { ButtonGroup, OverlayTrigger, ToggleButton, Tooltip } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { selectFerpaState } from '../../../../../features/authentication/authSlice';
import DetailedStatusGrid from '../../../../../components/DetailedStatusGrid';
import SortButtons from '../../../../../components/SortButtons';
import { useGetStudentSelfStudySessionsMutation } from '../../../../../features/authentication/reportApiSlice';
import { ResourceUtils } from '../../../../assignments/components/questionDetailComponents/ResourceUtils';
import { faTrophyStar } from '@fortawesome/pro-solid-svg-icons';
import SkeletonGrid from '../../../../../components/SkeletonLoader/SkeletonGrid';
import ActivityTypes from '../../../../../enums/activityTypes';

const SelfStudyStudentReport = () => {
  const navigate = useNavigate();
  let { studentId } = useParams();
  const location = useLocation();
  const { filters } = location.state;
  const [sortId, setSortId] = useState('date');
  const [isAscending, setIsAscending] = useState(true);
  const [sessionsData, setSessionsData] = useState([]);
  const [scoreType, setScoreType] = useState(0);
  const ferpaEnabled = useSelector(selectFerpaState);
  const [getStudentSessions, { data: reportData, isLoading }] = useGetStudentSelfStudySessionsMutation();
  const tableHeaderColumns = [
    {
      key: 'date',
      label: <>Created<br />Date</>,
      className: 'fw-medium cursor-pointer w-min-max-130',
      sortable: true,
    },
    {
      key: 'description',
      label: 'Description',
      className: 'fw-medium cursor-pointer w-min-max-250',
      sortable: true,
    },
    {
      key: 'completedDate',
      label: <>Completed<br />Date</>,
      className: 'fw-medium cursor-pointer w-min-max-130',
      sortable: true,
    },
    {
      key: 'createdVia',
      label: 'Via',
      className: 'fw-medium cursor-pointer w-min-max-80',
      sortable: true,
    },
    {
      key: 'course',
      label: 'Course',
      className: 'fw-medium cursor-pointer text-center',
      sortable: false,
    },
    {
      key: 'questionsCount',
      label: 'Q',
      className: 'fw-medium cursor-pointer text-center w-min-max-80',
      sortable: true,
    },
    {
      key: 'correctCount',
      label: <FontAwesomeIcon icon={faCheck} />,
      className: 'fw-medium cursor-pointer text-center w-min-max-80',
      sortable: true,
    },
    {
      key: 'incorrectCount',
      label: <FontAwesomeIcon icon={faClose} />,
      className: 'fw-medium cursor-pointer text-center w-min-max-80',
      sortable: true,
    },
    {
      key: 'correctOnRetryCount',
      label: <><FontAwesomeIcon icon={faClose} /><FontAwesomeIcon icon={faCheck} /></>,
      className: 'fw-medium cursor-pointer text-center w-min-max-80',
      sortable: true,
    },
    {
      key: 'crAnsweredCount',
      label: <>CR<br />Answered</>,
      className: 'fw-medium cursor-pointer text-center w-min-max-130',
      sortable: true,
    },
    {
      key: 'score',
      label: 'Score',
      className: 'fw-medium cursor-pointer text-center',
      sortable: false,
    }
  ];
  
  useEffect(() => {
    if (studentId) {
      getStudentSessions({ studentId: studentId, courseIds: filters?.courseIds });
    }
  }, []);

  useEffect(() => {
    if(reportData)
      setSessionsData(reportData?.studentSessions);
  }, [reportData]);

  const toggleOptions = [
    { tooltip: 'Percentage', value: 0, icon: '%' },
    { tooltip: 'Points', value: 1, icon: 'Pts' }
  ];

  useEffect(() => {
    if (reportData) {
      setSessionsData(reportData?.studentSessions);
    }
  }, [reportData]);

  const handleSort = async (currentSortId) => {
    let sortAsc = true;
    if (currentSortId === sortId)
      sortAsc = !isAscending;
    setSortId(currentSortId);

    let sortedStudents = ResourceUtils.customSort(sessionsData, currentSortId, sortAsc);
    setSessionsData(sortedStudents);
    setIsAscending(sortAsc);
  };

  const handleScoreTypeChange = (scoreType) => {
    setScoreType(Number(scoreType));
  };

  const combinedData = React.useMemo(() => {
    const totalSelfStudy = sessionsData?.length || 0;
    const completed = sessionsData?.reduce((sum, x) => sum + (x.completedDate != null ? 1 : 0), 0) || 0;
    const incompleted = sessionsData?.reduce((sum, s) => sum + (s.completedDate == null ? 1 : 0), 0) || 0;
    const completionRate = totalSelfStudy > 0 ? Math.round((completed / totalSelfStudy) * 100) : 0;
  
    return [
      { label: 'Total Self Study', value: totalSelfStudy },
      { label: 'Completed', value: completed },
      { label: 'Incomplete', value: incompleted },
      { label: 'Completion Rate', value: `${completionRate}%`, icon: <FontAwesomeIcon icon={faTrophyStar} size={20} className='m-0'/> }
    ];
  }, [sessionsData]);

  const handleNavigate = ()=>{
    let newfilters = {...filters, studentIds: [studentId], studentName: reportData?.name, classHeader: reportData?.studentClasses?.map(x => x.name).join(', ')};
    navigate('/reports/self-study/class-report/self_study_cumulativereport', { state: { filters : newfilters } });
  };

  return (
    <div className="white-card">
      <div className="row align-items-center mb-3">
        <div className="col-md-6">
          <h2 className="primary-title mb-md-0">
            {isLoading && <Skeleton />}
            {!isLoading && <h2 className='fw-medium'>Student Self Study Session Report</h2>}
          </h2>
        </div>
        <div className="col-md-6 d-flex gap-3 flex-column flex-sm-row  justify-content-between justify-content-md-end align-items-center">
          <div className="col-xl-1 text-xl-end ps-xl-0 mt-3 mt-xl-0 mr-2">
            <FERPA />
          </div>
          <button
            onClick={() => window.history.back()}
            className="btn btn-castleWhite btn-width">
            Back
          </button>
        </div>
      </div>

      <div className="custom-table table-responsive">
        <div className="student_response_details">
          <div className="d-flex justify-content-between">
            {!isLoading ? (
              <>
                <div className="student_response_details_left">
                  <p>
                    <strong>Student Name:&nbsp;</strong>{ferpaEnabled? reportData?.name : 'name hidden'}
                  </p>
                </div>
                <div className="student_response_details_right">
                  <p>
                    <strong>Class{reportData?.studentClasses?.length > 0 ? 's' : ''}:&nbsp;</strong>
                    {reportData?.studentClasses[0]?.name}
                    {reportData?.studentClasses?.length > 1 && (
                      <>
                        &nbsp;and&nbsp;
                        <OverlayTrigger
                          placement='left'
                          overlay={
                            <Tooltip className='initial-assignment-tooltip'>
                              {reportData?.studentClasses.map(x => x.name).join(', ')}
                            </Tooltip>
                          }
                        >
                          <span className="ms-1 text-castleOrange" style={{ cursor: 'pointer' }}>
                            {`${reportData?.studentClasses?.length - 1} classes more`}
                          </span>
                        </OverlayTrigger>
                      </>
                    )}
                  </p>
                </div>
              </>
            ):(
              <>
                <div className="m-0 w-100 row">
                  <div className='col-6 mb-3'>
                    <Skeleton />
                  </div>
                  <div className='col-6 mb-3'>
                    <Skeleton />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
        <div className='student_response_details'>
          {isLoading ? <Skeleton className='h-35 w-min-max-250' /> : <h5 className='fs-16'>Cumulative Summary</h5>}
          <DetailedStatusGrid
            isLoading={isLoading}
            data={combinedData}
            showLink={false}
          />
        </div>
        {isLoading ?
          <>
            <SkeletonGrid
              rowCount={1}
              colCount={2}
              className={'pb-2 mb-4'}
            />
            <TableSkeleton
              colCount={8}
              rowCount={4} />
          </>
          :
          <>
            <div className='d-flex justify-content-between'>  
              <button className='btn btn-castleOrange' onClick={() => handleNavigate()}>View Individual Cumulative Report</button>
              <div className='align-items-center d-flex justify-content-end mb-4 mt-2 no-select'>Display score as&nbsp; 
                <ButtonGroup className='pt-1 z-index10 bg-white'>
                  {toggleOptions.map((option, index) => (
                    <>
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip className="custom-tooltip">{option.tooltip}</Tooltip>
                        }>
                        <ToggleButton
                          className='btn-outline-castleRed align-content-center h-35 w-40'
                          key={`score-type-toggle-${index}`}
                          id={`score-type-toggle-${index}`}
                          type="radio"
                          variant=''
                          name='score-toggle-btn'
                          value={option.value}
                          checked={scoreType == option.value}
                          onChange={(e)=> handleScoreTypeChange(e.target.value)}
                        >
                          {option.icon}
                        </ToggleButton>
                      </OverlayTrigger>
                    </>            
                  ))}
                </ButtonGroup>
              </div>
            </div>
            <table className='table reportTable sessionTable'>
              <thead className="thead-light align-middle">
                <tr>
                  {tableHeaderColumns.map((col) => (
                    <th key={col.key} className={col.className} onClick={col.sortable ? () => handleSort(col.key) : undefined}>
                      {col.label}
                      {col.sortable && (
                        <SortButtons
                          selectedSortId={sortId}
                          elementSortId={col.key}
                          isAscending={isAscending}
                        />
                      )}
                    </th>
                  ))}
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {sessionsData?.map((item) => {
                  var assignmentScore = item.score;
                  if(item.score == null)
                  {
                    assignmentScore = item.scoreStatus;
                  }
                  else{
                    if (scoreType === 1) {
                      assignmentScore = item.score.points;
                    } else {
                      assignmentScore = item.score.percent;
                    }
                  }

                  return (
                    <>
                      <tr key={`${item.sessionId}-key`}>
                        <td className='text-center w-min-max-130'>{DateFormatter.toLongDateTime(item.date, false, false)}</td>
                        <td className='text-center w-min-max-250'>{item.description}</td>
                        <td className='text-center w-min-max-130'>{DateFormatter.toLongDateTime(item.completedDate, false, false)}</td>
                        <td className='text-center w-min-max-80'>{item.createdVia}</td>
                        <td className='text-center'>{item.courses?.name}</td>
                        <td className='text-center w-min-max-80'>{item.questionsCount}</td>
                        <td className='text-center w-min-max-80'>{item.correctCount}</td>
                        <td className='text-center w-min-max-80'>{item.incorrectCount}</td>
                        <td className='text-center w-min-max-80'>{item.correctOnRetryCount}</td>
                        <td className='text-center w-min-max-130'>{item.crAnsweredCount}</td>
                        <td className='text-center'>{assignmentScore}</td>
                        <td>
                          <div className='d-flex flex-row gap-2'>
                            <NavLink to={`/responses/0/${ActivityTypes.Assignment}/${studentId}/${item.sessionId}/true`}>
                              <OverlayTrigger
                                key="viewSessions"
                                placement="top"
                                overlay={<Tooltip className="custom-tooltip">Responses</Tooltip>}>
                                <button
                                  id={'viewSessions' + item.id}
                                  className="btn grid-icon-btn action-btn-tile blue"
                                >
                                  <FontAwesomeIcon icon={faMessagesQuestion} />
                                </button>
                              </OverlayTrigger>
                            </NavLink>
                            <NavLink to={`/assignments/${item.sessionId}/manage_assign/reports/student_session_report?studentId=${studentId}&studentsToInclude=2&isSelfStudy=true`}>
                              <OverlayTrigger
                                key="viewSessions"
                                placement="top"
                                overlay={<Tooltip className="custom-tooltip">Session Report</Tooltip>}>
                                <button
                                  id={'viewSessions' + item.id}
                                  className="btn grid-icon-btn action-btn-tile blue"
                                >
                                  <FontAwesomeIcon icon={faChartArea} />
                                </button>
                              </OverlayTrigger>
                            </NavLink>
                          </div>
                        </td>
                      </tr>
                    </>
                  );
                })}
              </tbody>
            </table>
          </>
        }
      </div>
    </div>
  );
};

export default SelfStudyStudentReport;